import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import oDayJs from "dayjs";

import {
  actionConfigOptimizeCfe,
  actionConfigUpdateNumbers,
  actionConfigImportCfe,
  actionConfigGetParameter,
  actionConfigUpdateParameter,
  selectConfigState,
} from "./redux";

import { BUTTON_CONFIG } from "config/constants/Button";
import { Button, ConfirmationModal, Input, ScreenContainer, Text, DateInput } from "components";

const ACTIONS = {
  UPDATE_NUMBERS: "UPDATE_NUMBERS",
  OPTIMIZE_CFE: "OPTIMIZE_CFE",
  IMPORT_CFE: "IMPORTAR_COMPRAS",
};

const SECTIONS = {
  ACTIONS: "ACTIONS",
  DEFAULTS: "DEFAULTS",
  LICENSING: "LICENSING",
};

const Config = () => {
  const oDispatch = useDispatch();

  const [sAction, setAction] = useState("");
  const [bShowModal, setShowModal] = useState(false);
  const [sOpenSection, setOpenSection] = useState(null);

  const { aParameters } = useSelector(selectConfigState);
  const [sListaXDefecto, setListaXDefecto] = useState("");
  const [sSocGralId, setSocGralId] = useState("");
  const [dFchVncLicencia, setFchVncLicencia] = useState(null);

  const onUpdateNumbers = (bConfirm) => {
    if (!bConfirm) {
      setAction(ACTIONS.UPDATE_NUMBERS);
      return setShowModal(true);
    }
    oDispatch(actionConfigUpdateNumbers());
  };

  const onOptimizeCfe = (bConfirm) => {
    if (!bConfirm) {
      setAction(ACTIONS.OPTIMIZE_CFE);
      return setShowModal(true);
    }
    oDispatch(actionConfigOptimizeCfe());
  };

  const onImportCfe = (bConfirm) => {
    if (!bConfirm) {
      setAction(ACTIONS.IMPORT_CFE);
      return setShowModal(true);
    }
    oDispatch(actionConfigImportCfe());
  };

  const resolveCallback = () => {
    switch (sAction) {
      case ACTIONS.UPDATE_NUMBERS: onUpdateNumbers(true); break;
      case ACTIONS.OPTIMIZE_CFE: onOptimizeCfe(true); break;
      case ACTIONS.IMPORT_CFE: onImportCfe(true); break;
      default: break;
    }
    setShowModal(false);
    setAction("");
  };

  const resolveTitle = () => {
    switch (sAction) {
      case ACTIONS.UPDATE_NUMBERS: return "Actualizar numeradores";
      case ACTIONS.OPTIMIZE_CFE: return "Optimizar tabla de envíos de CFE";
      case ACTIONS.IMPORT_CFE: return "Importar compras";
      default: return "";
    }
  };

  const resolveQuestion = () => {
    switch (sAction) {
      case ACTIONS.UPDATE_NUMBERS: return "Está seguro que desea actualizar los numeradores";
      case ACTIONS.OPTIMIZE_CFE: return "Está seguro que desea optimizar la tabla de envíos de CFE";
      case ACTIONS.IMPORT_CFE: return "Está seguro que desea importar las compras recibidas";
      default: return "";
    }
  };

  useEffect(() => {
    oDispatch(actionConfigGetParameter("ListaXDefecto"));
    oDispatch(actionConfigGetParameter("SocGralId"));
    oDispatch(actionConfigGetParameter("FchVncLicencia"));
  }, []);

  useEffect(() => {
    const oParam = aParameters.find(p => p.parNom === "ListaXDefecto");
    if (oParam) setListaXDefecto(oParam.parVal);
    const oParamSoc = aParameters.find(p => p.parNom === "SocGralId");
    if (oParamSoc) setSocGralId(oParamSoc.parVal);
    const oParamFch = aParameters.find(p => p.parNom === "FchVncLicencia");
    if (oParamFch) setFchVncLicencia(oDayJs(oParamFch.parVal).toDate());
  }, [aParameters]);

  const onSaveListaXDefecto = () => {
    oDispatch(actionConfigUpdateParameter({ sName: "ListaXDefecto", sValue: sListaXDefecto }));
  };

  const onSaveSocGralId = () => {
    oDispatch(actionConfigUpdateParameter({ sName: "SocGralId", sValue: sSocGralId }));
  };

  const onSaveFchVncLicencia = () => {
    if (!dFchVncLicencia) return;
    const sFormatted = oDayJs(dFchVncLicencia).format("YYYY-MM-DD");
    oDispatch(actionConfigUpdateParameter({ sName: "FchVncLicencia", sValue: sFormatted }));
  };

  const onToggleSection = (sSection) => {
    setOpenSection(prev => prev === sSection ? null : sSection);
  };

  const AccordionHeader = ({ title, section }) => (
    <button
      type="button"
      onClick={() => onToggleSection(section)}
      className="w-full flex justify-between items-center px-5 py-4 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
    >
      <Text bold size="text-base">{title}</Text>
      <span className="text-xl text-gray-500 dark:text-gray-300">
        {sOpenSection === section ? "▲" : "▼"}
      </span>
    </button>
  );

  return (
    <ScreenContainer>
      <section className="grid grid-cols-2">
        <h1 className="ui-page-title self-center">
          Configuración
        </h1>
      </section>
      <section className="flex flex-col gap-4 pt-10">

        {/* ACCIONES */}
        <div className="rounded-lg border dark:border-gray-700 overflow-hidden">
          <AccordionHeader title="Acciones" section={SECTIONS.ACTIONS} />
          {sOpenSection === SECTIONS.ACTIONS && (
            <div className="flex flex-col gap-5 px-5 py-4">
              <div className="flex items-center border-b-2 py-1">
                <Text bold size="text-base">Actualizar numeradores:</Text>
                <Button
                  label="Actualizar"
                  size={BUTTON_CONFIG.SIZE.SM}
                  containerClassName="ml-auto"
                  onClick={() => onUpdateNumbers()}
                  type={BUTTON_CONFIG.CLASS.PRIMARY}
                />
              </div>
              <div className="flex items-center border-b-2 py-1">
                <Text bold size="text-base">Optimizar tabla de envíos de CFE:</Text>
                <Button
                  label="Optimizar"
                  size={BUTTON_CONFIG.SIZE.SM}
                  containerClassName="ml-auto"
                  onClick={() => onOptimizeCfe()}
                  type={BUTTON_CONFIG.CLASS.SUCCESS}
                />
              </div>
              <div className="flex items-center py-1">
                <Text bold size="text-base">Importar compras pendientes:</Text>
                <Button
                  label="Importar"
                  size={BUTTON_CONFIG.SIZE.SM}
                  containerClassName="ml-auto"
                  onClick={() => onImportCfe()}
                  type={BUTTON_CONFIG.CLASS.SUCCESS}
                />
              </div>
            </div>
          )}
        </div>

        {/* POR DEFECTO */}
        <div className="rounded-lg border dark:border-gray-700 overflow-hidden">
          <AccordionHeader title="Por defecto" section={SECTIONS.DEFAULTS} />
          {sOpenSection === SECTIONS.DEFAULTS && (
            <div className="flex flex-col gap-5 px-5 py-4">
              <div className="flex items-center border-b-2 py-1 gap-5">
                <Text bold size="text-base">Lista de precios por defecto:</Text>
                <Input
                  className="w-40 text-right"
                  value={sListaXDefecto}
                  onChange={setListaXDefecto}
                  placeholder="ID de lista"
                />
                <Button
                  label="Guardar"
                  size={BUTTON_CONFIG.SIZE.SM}
                  containerClassName="ml-auto"
                  onClick={onSaveListaXDefecto}
                  type={BUTTON_CONFIG.CLASS.PRIMARY}
                />
              </div>
              <div className="flex items-center py-1 gap-5">
                <Text bold size="text-base">Socio de negocio por defecto:</Text>
                <Input
                  className="w-40 text-right"
                  value={sSocGralId}
                  onChange={setSocGralId}
                  placeholder="ID de socio"
                />
                <Button
                  label="Guardar"
                  size={BUTTON_CONFIG.SIZE.SM}
                  containerClassName="ml-auto"
                  onClick={onSaveSocGralId}
                  type={BUTTON_CONFIG.CLASS.PRIMARY}
                />
              </div>
            </div>
          )}
        </div>

        {/* LICENCIAMIENTO */}
        <div className="rounded-lg border dark:border-gray-700 overflow-hidden">
          <AccordionHeader title="Licenciamiento" section={SECTIONS.LICENSING} />
          {sOpenSection === SECTIONS.LICENSING && (
            <div className="flex flex-col gap-5 px-5 py-4">
              <div className="flex items-center py-1 gap-5">
                <Text bold size="text-base">Fecha de vencimiento de licencia:</Text>
                <DateInput
                  value={dFchVncLicencia}
                  onChange={setFchVncLicencia}
                  className="w-48"
                  labelClass="hidden"
                  placeholder="Fecha de vencimiento"
                />
                <Button
                  label="Guardar"
                  size={BUTTON_CONFIG.SIZE.SM}
                  containerClassName="ml-auto"
                  onClick={onSaveFchVncLicencia}
                  type={BUTTON_CONFIG.CLASS.PRIMARY}
                />
              </div>
            </div>
          )}
        </div>

      </section>
      <ConfirmationModal
        show={bShowModal}
        title={resolveTitle()}
        onConfirm={resolveCallback}
        question={resolveQuestion()}
        message="Esta acción puede tardar unos minutos"
        onClose={() => { setShowModal(false); setAction(""); }}
      />
    </ScreenContainer>
  );
};

export default Config;
